/**
 * WelcomeAuth — first screen for unauthenticated users. Mirrors RN's
 * `apps/WunderGuide/views/WelcomeAuth.tsx`: SSO buttons, Sign In, Register,
 * and Continue as Guest.
 *
 * Apple SSO is iOS-only on RN — never rendered on web. Google SSO uses the
 * existing GoogleSignInButton wired in SignIn.tsx.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { useToast } from '../context/ToastContext';
import config from '../../config/env.json';

import Button from '../common/Button';
import Spinner from '../common/Spinner';
import Text from '../common/Text';
import { GoogleSignInButton, SSODivider } from '../common/SSOButtons';
import './WelcomeAuth.css';

const GOOGLE_CLIENT_ID =
  ((window as any).__GOOGLE_CLIENT_ID__ as string | undefined) ||
  (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ||
  (config as any).GOOGLE_CLIENT_ID ||
  '';

const WelcomeAuth = () => {
  const { t, Logo } = useLanguage();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { createGuest, loginWithGoogle, authState } = useAuth();

  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isProcessingRef = useRef(false);
  const googleInitializedRef = useRef(false);

  const isSSOEnabled = !!GOOGLE_CLIENT_ID;
  const anyLoading = isGuestLoading || isGoogleLoading;

  useEffect(() => {
    if (authState === 'authenticated' || authState === 'guest') {
      navigate('/');
    }
  }, [authState, navigate]);

  const handleGoogleResponse = useCallback(
    async (response: { credential: string }) => {
      setError(null);
      try {
        await loginWithGoogle(response.credential);
        navigate('/');
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Google sign-in failed';
        setError(msg);
        showToast(msg, { variant: 'error' });
      } finally {
        setIsGoogleLoading(false);
        isProcessingRef.current = false;
      }
    },
    [loginWithGoogle, navigate, showToast],
  );

  useEffect(() => {
    if (!isSSOEnabled || googleInitializedRef.current) return;
    const init = () => {
      const w = window as any;
      if (!w.google?.accounts?.id) return;
      w.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
      });
      googleInitializedRef.current = true;
    };
    if ((window as any).google?.accounts?.id) {
      init();
      return;
    }
    const interval = setInterval(() => {
      if ((window as any).google?.accounts?.id) {
        clearInterval(interval);
        init();
      }
    }, 100);
    return () => clearInterval(interval);
  }, [isSSOEnabled, handleGoogleResponse]);

  const handleContinueAsGuest = async () => {
    if (isGuestLoading || isProcessingRef.current) return;
    try {
      isProcessingRef.current = true;
      setIsGuestLoading(true);
      setError(null);
      await createGuest();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        t('GUEST_SESSION_FAILED') ||
        'Failed to create guest session';
      setError(msg);
      showToast(msg, { variant: 'error' });
    } finally {
      setIsGuestLoading(false);
      isProcessingRef.current = false;
    }
  };

  const handleGoogleSignIn = () => {
    if (isGoogleLoading || isProcessingRef.current) return;
    const w = window as any;
    if (!w.google?.accounts?.id) return;
    isProcessingRef.current = true;
    setIsGoogleLoading(true);
    setError(null);
    w.google.accounts.id.prompt();
  };

  return (
    <div className="welcome-auth">
      <div className="welcome-auth-card">
        <div className="welcome-auth-logo" aria-hidden>
          <Logo />
        </div>

        {isSSOEnabled && (
          <div className="welcome-auth-sso">
            <GoogleSignInButton
              onClick={handleGoogleSignIn}
              loading={isGoogleLoading}
              disabled={anyLoading}
            />
            <SSODivider />
          </div>
        )}

        <div className="welcome-auth-buttons">
          <Button
            title={t('SIGN_IN') || 'Sign in'}
            variant="filled"
            onClick={() => navigate('/signin')}
            disabled={anyLoading}
          />
          <Button
            title={t('REGISTER') || 'Register'}
            variant="outlined"
            onClick={() => navigate('/register')}
            disabled={anyLoading}
          />
        </div>

        {error && (
          <div className="welcome-auth-error">
            <Text variant="body">{error}</Text>
          </div>
        )}

        <div className="welcome-auth-guest">
          {isGuestLoading ? (
            <div className="welcome-auth-guest-loading">
              <Spinner />
              <span>
                {t('CREATING_GUEST_SESSION') || 'Creating guest session…'}
              </span>
            </div>
          ) : (
            <Button
              title={t('CONTINUE_AS_GUEST') || 'Continue as guest'}
              variant="text"
              onClick={handleContinueAsGuest}
              disabled={anyLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeAuth;
